import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiGet } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { nmFace } from "./nemesis.js";

/**
 * Dynasty (backend/dynasty.py), inside the CEO Office.
 *
 *   the house     generation, dynasty score, the bonuses its traits give today
 *   lineage       every CEO so far, with their era score and the legacy trait
 *                 they handed down; the sitting CEO shows what they'd leave if
 *                 they stepped down now
 *   traits        the newest three are active, older ones fade into history
 *   succession    groom a successor, then hand over (the same engine actions
 *                 as before: /api/succession/*)
 */
const dyFaceSeed = (name) => [...String(name)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
const dyStat = (label, v) => `<div class="dy-stat"><span>${label}</span><i><b style="width:${Math.max(0, Math.min(100, v || 0))}%"></b></i><em>${Math.round(v || 0)}</em></div>`;
const dyTrait = (t, cls = "") => `<span class="dy-trait ${cls} ${t.kind === "growth" && t.value < 0 ? "neg" : ""}" title="${esc(t.text)}">${esc(t.icon)} ${esc(t.name)}${t.strength > 1 ? " ×1.5" : ""}</span>`;

async function renderDynastyInto(host) {
  const d = await uiGet("/dynasty");
  const s = d.succession, cand = s.candidate, now = d.if_you_hand_over_now;
  const b = d.bonuses;
  const bonusTxt = [b.growth_pts ? `growth ${b.growth_pts > 0 ? "+" : ""}${b.growth_pts} pts/mo` : "", b.deal_pct ? `deals +${b.deal_pct}%` : "",
    b.compliance_pts ? `compliance floor +${b.compliance_pts}` : ""].filter(Boolean).join(" · ") || "no inherited bonuses yet";
  host.innerHTML = `<div class="dy">
    <div class="dy-house"><div><div class="dy-kick">Generation ${d.generation}${d.dynasty_score != null ? ` · dynasty score ${d.dynasty_score}` : ""}</div>
      <h3>👑 ${esc(d.house)}</h3><p>Inherited today: <b>${esc(bonusTxt)}</b></p></div>
      <div class="dy-active">${d.active_traits.length ? d.active_traits.map((t) => dyTrait(t)).join("") : `<span class="dy-none">Hand the company over to start the line's legacy.</span>`}</div></div>
    <ol class="dy-line">${d.lineage.map((g) => {
      const cur = g.to == null;
      return `<li class="dy-gen${cur ? " now" : ""}">
        <div class="dy-face">${nmFace(dyFaceSeed(g.name), 64)}</div>
        <div class="dy-gen-body"><span class="dy-kick">${cur ? "Sitting CEO" : `Generation ${g.generation}`} · M${g.from}${g.to != null ? `–M${g.to}` : "–now"}</span>
          <b>${esc(g.name)}</b><em>${esc(g.archetype)}</em>
          ${g.era_score != null ? `<div class="dy-era"><i style="width:${g.era_score}%"></i><span>era ${g.era_score}</span></div>` : ""}
          ${g.legacy ? `<div>Left: ${dyTrait(g.legacy)}</div>` : ""}
          ${cur && now ? `<div class="dy-would">If they handed over now: era ${now.era_score_now} → leaves ${dyTrait(now.would_leave, "ghost")}</div>` : ""}</div></li>`;
    }).join("")}</ol>
    <div class="dy-seat">
      <div class="dy-card"><h4>The CEO today</h4><b>${esc(s.current_ceo.name)}</b><em>${esc(s.current_ceo.archetype)}</em>
        ${dyStat("Vision", s.current_ceo.vision)}${dyStat("Operations", s.current_ceo.operations)}${dyStat("Dealmaking", s.current_ceo.dealmaking)}${dyStat("Stewardship", s.current_ceo.stewardship)}</div>
      <div class="dy-card">${cand ? `<h4>Successor in training</h4><b>${esc(cand.name)}</b><em>${esc(cand.archetype)} · groomed ${cand.months_groomed} month${cand.months_groomed === 1 ? "" : "s"}</em>
          ${dyStat("Vision", cand.vision)}${dyStat("Operations", cand.operations)}${dyStat("Dealmaking", cand.dealmaking)}${dyStat("Stewardship", cand.stewardship)}
          <div class="dy-actions"><button type="button" class="primary" data-dy="appoint" ${cand.can_appoint ? "" : "disabled"}>Hand over the company</button><button type="button" data-dy="dismiss">Let them go</button></div>
          ${cand.can_appoint ? "" : `<p class="dy-note">Ready to take over after 3 months of grooming.</p>`}`
        : `<h4>No successor yet</h4><p class="dy-note">Grooming takes at least 3 months and costs a search fee. Each successor brings one of four leadership styles.</p>
          <div class="dy-actions"><button type="button" class="primary" data-dy="groom">Groom a successor</button></div>`}</div>
    </div>
    <p class="dy-note">${esc(d.rules)}</p></div>`;
  host.querySelectorAll("[data-dy]").forEach((btn) => btn.addEventListener("click", async () => {
    const act = btn.dataset.dy;
    if (act === "appoint" && !window.confirm(`Hand ${d.house.replace("House of ", "")} to ${cand.name}? ${s.current_ceo.name} steps down for good.`)) return;
    btn.disabled = true;
    try { toast((await postJSON(`/succession/${act}`, {})).message); } catch (e) { toast(e.message || "That didn't work.", true); }
    await window.refreshAll();
  }));
}

window.renderDynastyInto = renderDynastyInto;

export { renderDynastyInto };
